import React, { useState, useEffect } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { api, formatApiError } from "../lib/api";
import { Search, BookOpen, GraduationCap, Video, HelpCircle, Calendar, Clock, ArrowLeft, Star } from "lucide-react";

const TYPE_META = {
  blog: { label: "Blog", icon: BookOpen, color: "#0891B2", route: "/resources/blog" },
  guide: { label: "Guide", icon: GraduationCap, color: "#8B5CF6", route: "/resources/guides" },
  tutorial: { label: "Tutorial", icon: Video, color: "#F59E0B", route: "/resources/tutorials" },
};

const sanitize = (html) =>
  DOMPurify.sanitize(html || "", {
    ADD_ATTR: ["target", "rel"],
    FORBID_TAGS: ["style", "iframe", "script"],
    FORBID_ATTR: ["onerror", "onload", "onclick"],
  });

/** Imperative SEO helper — mutates document.head directly so we get exactly
 *  ONE tag per name (react-helmet-async v3 + React 19 concurrent renders
 *  don't reliably dedupe). Restores previous values on unmount. */
function setMetaTag(attrName, attrValue, contentValue) {
  let el = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
  const prev = el ? el.getAttribute("content") : null;
  const created = !el;
  if (created) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", contentValue || "");
  return () => {
    if (created) {
      el.remove();
    } else if (prev != null) {
      el.setAttribute("content", prev);
    }
  };
}

function setLinkTag(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const prev = el ? el.getAttribute("href") : null;
  const created = !el;
  if (created) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return () => {
    if (created) {
      el.remove();
    } else if (prev != null) {
      el.setAttribute("href", prev);
    }
  };
}

function SEOHead({ title, description, image, keywords, canonical }) {
  useEffect(() => {
    const fullTitle = title ? `${title} — Trinity Layers` : "Trinity Layers";
    const prevTitle = document.title;
    document.title = fullTitle;

    const cleanups = [];
    if (description) cleanups.push(setMetaTag("name", "description", description));
    if (keywords?.length) cleanups.push(setMetaTag("name", "keywords", keywords.join(", ")));
    cleanups.push(setMetaTag("property", "og:title", fullTitle));
    if (description) cleanups.push(setMetaTag("property", "og:description", description));
    if (image) {
      cleanups.push(setMetaTag("property", "og:image", image));
      cleanups.push(setMetaTag("name", "twitter:image", image));
    }
    cleanups.push(setMetaTag("property", "og:type", "article"));
    cleanups.push(setMetaTag("name", "twitter:card", "summary_large_image"));
    if (canonical) cleanups.push(setLinkTag("canonical", canonical));

    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn());
    };
  }, [title, description, image, keywords, canonical]);

  return null;
}

/* ─── Hub page: /resources ─────────────────────────────────────────── */
export function ResourcesHub() {
  const cats = [
    { ...TYPE_META.blog, description: "News, product updates, industry insights and Trinity Layers stories.", href: "/resources/blog" },
    { ...TYPE_META.guide, description: "Deep guides on 3D printing, material selection, workflow and pricing.", href: "/resources/guides" },
    { ...TYPE_META.tutorial, description: "Step-by-step tutorials for designers, makers and print farms.", href: "/resources/tutorials" },
    { label: "FAQs", icon: HelpCircle, color: "#22C55E", description: "Answers to the most common questions from customers, experts and partners.", href: "/resources/faqs" },
  ];
  return (
    <div data-testid="resources-hub" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEOHead title="Resources" description="Blog, guides, tutorials and FAQs from Trinity Layers — the 3D creation and manufacturing marketplace." />
      <div className="mb-10">
        <div className="tl-label mb-2">Resources</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-3">Learn, level up, ship faster</h1>
        <p className="text-[#475569] max-w-2xl">Hand-picked blog posts, in-depth guides, video tutorials and answers to the questions the Trinity Layers community asks most.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cats.map((c) => (
          <Link key={c.label} to={c.href} data-testid={`resources-hub-${c.label.toLowerCase()}`} className="tl-card p-5 hover:border-[#0891B2]/40 transition-colors">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: `${c.color}22`, border: `1px solid ${c.color}55` }}>
              <c.icon className="w-5 h-5" style={{ color: c.color }} />
            </div>
            <div className="font-display font-semibold text-lg mb-1">{c.label}</div>
            <p className="text-sm text-[#475569] leading-relaxed">{c.description}</p>
          </Link>
        ))}
      </div>
      <FeaturedStrip />
    </div>
  );
}

function FeaturedStrip() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/resources?featured=true&limit=3").then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (items.length === 0) return null;
  return (
    <div className="mt-14">
      <div className="flex items-center gap-2 mb-4 tl-label"><Star className="w-3 h-3" /> Featured</div>
      <div className="grid md:grid-cols-3 gap-4">{items.map((r) => <ResourceCard key={r.id} r={r} />)}</div>
    </div>
  );
}

/* ─── List page: /resources/blog | /resources/guides | /resources/tutorials */
export function ResourceList({ type }) {
  const meta = TYPE_META[type];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";

  useEffect(() => {
    let active = true;
    setLoading(true);
    const p = new URLSearchParams({ type });
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    api.get(`/resources?${p.toString()}`).then((r) => { if (active) setItems(r.data); }).catch(() => { if (active) setItems([]); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [type, q, category]);

  const setParam = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); setParams(n); };
  const categories = Array.from(new Set(items.map((i) => i.category).filter(Boolean))).sort();

  return (
    <div data-testid={`resource-list-${type}`} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEOHead title={`${meta.label}s`} description={`Trinity Layers ${meta.label.toLowerCase()}s — expert content for 3D creation and manufacturing.`} />
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <Link to="/resources" className="tl-label mb-2 inline-flex items-center gap-1 hover:text-slate-900 transition-colors"><ArrowLeft className="w-3 h-3" /> All resources</Link>
          <h1 className="text-3xl sm:text-4xl font-display font-bold flex items-center gap-3">
            <meta.icon className="w-8 h-8" style={{ color: meta.color }} />
            {meta.label}s
          </h1>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
          <input data-testid={`${type}-search`} defaultValue={q} onKeyDown={(e) => e.key === "Enter" && setParam("q", e.target.value)} placeholder={`Search ${meta.label.toLowerCase()}s…`} className="tl-input pl-10" />
        </div>
      </div>

      {categories.length > 0 && (
        <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
          <button data-testid={`${type}-cat-all`} onClick={() => setParam("category", "")} className={`shrink-0 px-3 py-1.5 rounded-full text-xs border ${!category ? "bg-[#2563EB] border-[#2563EB]" : "border-slate-200 text-[#475569] hover:text-slate-900"}`}>All</button>
          {categories.map((c) => (
            <button key={c} data-testid={`${type}-cat-${c}`} onClick={() => setParam("category", c)} className={`shrink-0 px-3 py-1.5 rounded-full text-xs border ${category === c ? "bg-[#2563EB] border-[#2563EB]" : "border-slate-200 text-[#475569] hover:text-slate-900"}`}>{c}</button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-20 text-[#475569]">Loading…</div>
      ) : items.length === 0 ? (
        <div data-testid={`no-${type}s`} className="text-center py-20">
          <div className="text-2xl font-display font-semibold mb-2">No {meta.label.toLowerCase()}s yet</div>
          <div className="text-[#475569]">Check back soon — new content is added regularly.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((r) => <ResourceCard key={r.id} r={r} />)}
        </div>
      )}
    </div>
  );
}

function ResourceCard({ r }) {
  const meta = TYPE_META[r.type] || TYPE_META.blog;
  return (
    <Link to={`/resources/${r.slug}`} data-testid={`resource-card-${r.slug}`} className="tl-card overflow-hidden group flex flex-col hover:border-[#0891B2]/40 transition-colors">
      {r.featured_image && (
        <div className="aspect-[16/9] overflow-hidden bg-[#FFFFFF]">
          <img src={r.featured_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2 text-[10px] tl-label">
          <span style={{ color: meta.color }}>{meta.label}</span>
          {r.category && <span className="text-[#64748B]">· {r.category}</span>}
          {r.featured && <span className="text-[#F59E0B] flex items-center gap-1"><Star className="w-2.5 h-2.5 fill-[#F59E0B]" /> Featured</span>}
        </div>
        <h3 className="font-display font-semibold text-lg mb-2 line-clamp-2">{r.title}</h3>
        {r.excerpt && <p className="text-sm text-[#475569] line-clamp-3 mb-4">{r.excerpt}</p>}
        <div className="mt-auto pt-3 flex items-center gap-4 text-xs text-[#64748B] border-t border-slate-200">
          {r.published_at && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(r.published_at).toLocaleDateString()}</span>}
          {r.reading_minutes ? <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.reading_minutes} min</span> : null}
        </div>
      </div>
    </Link>
  );
}

/* ─── Detail page: /resources/:slug ────────────────────────────────── */
export function ResourceDetail() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/resources/${slug}`).then((res) => setR(res.data)).catch(() => setErr(true));
  }, [slug]);
  if (err) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><SEOHead title="Not found" /><h1 className="text-2xl font-display font-bold mb-3">Not found</h1><p className="text-[#475569]">This resource is unavailable or has been unpublished.</p><Link to="/resources" className="tl-btn-primary inline-flex mt-6">Back to resources</Link></div>;
  if (!r) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-[#475569]">Loading…</div>;
  const meta = TYPE_META[r.type] || TYPE_META.blog;
  const canonical = `${window.location.origin}/resources/${r.slug}`;
  return (
    <article data-testid="resource-detail" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEOHead
        title={r.seo_title || r.title}
        description={r.seo_description || r.excerpt}
        image={r.social_image || r.featured_image}
        keywords={r.seo_keywords}
        canonical={canonical}
      />
      <Link to={meta.route} className="tl-label inline-flex items-center gap-1 mb-4 hover:text-slate-900 transition-colors"><ArrowLeft className="w-3 h-3" /> {meta.label}s</Link>
      <div className="flex items-center gap-2 mb-3 text-xs">
        <span className="tl-chip" style={{ color: meta.color, borderColor: `${meta.color}55` }}>{meta.label}</span>
        {r.category && <span className="tl-chip">{r.category}</span>}
      </div>
      <h1 data-testid="resource-title" className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4">{r.title}</h1>
      <div className="flex items-center gap-4 text-sm text-[#475569] mb-8 pb-6 border-b border-slate-200">
        {r.author && <span>By {r.author}</span>}
        {r.published_at && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(r.published_at).toLocaleDateString()}</span>}
        {r.reading_minutes ? <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.reading_minutes} min read</span> : null}
      </div>
      {r.featured_image && <img src={r.featured_image} alt={r.title} className="w-full rounded-xl mb-8" />}
      <div data-testid="resource-content" className="tl-prose" dangerouslySetInnerHTML={{ __html: sanitize(r.content) }} />
      {r.tags?.length > 0 && (
        <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-2">
          {r.tags.map((t) => <span key={t} className="tl-chip">#{t}</span>)}
        </div>
      )}
    </article>
  );
}

/* ─── FAQs page: /resources/faqs ───────────────────────────────────── */
export function FAQsPage() {
  const [faqs, setFaqs] = useState([]);
  const [open, setOpen] = useState({});
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => setFaqs([])); }, []);
  const filtered = faqs.filter((f) => !q || f.question.toLowerCase().includes(q.toLowerCase()) || f.answer.toLowerCase().includes(q.toLowerCase()));
  const grouped = filtered.reduce((acc, f) => { const c = f.category || "General"; (acc[c] = acc[c] || []).push(f); return acc; }, {});
  return (
    <div data-testid="faqs-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEOHead title="FAQs" description="Frequently asked questions about Trinity Layers, our experts, 3D printer owners, orders and payments." />
      <Link to="/resources" className="tl-label inline-flex items-center gap-1 mb-4 hover:text-slate-900 transition-colors"><ArrowLeft className="w-3 h-3" /> All resources</Link>
      <h1 className="text-3xl sm:text-4xl font-display font-bold mb-4">Frequently asked questions</h1>
      <p className="text-[#475569] mb-6">Can't find what you're looking for? <Link to="/contact" className="text-[#0891B2]">Contact us</Link>.</p>
      <div className="relative mb-8">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
        <input data-testid="faq-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions…" className="tl-input pl-10" />
      </div>
      {Object.keys(grouped).length === 0 ? (
        <div data-testid="no-faqs" className="text-center py-16 text-[#475569]">No FAQs available yet.</div>
      ) : (
        Object.entries(grouped).map(([cat, list]) => (
          <div key={cat} className="mb-8">
            <div className="tl-label mb-3">{cat}</div>
            <div className="space-y-2">
              {list.map((f) => {
                const isOpen = !!open[f.id];
                return (
                  <div key={f.id} data-testid={`faq-item-${f.id}`} className="tl-card overflow-hidden">
                    <button onClick={() => setOpen({ ...open, [f.id]: !isOpen })} data-testid={`faq-toggle-${f.id}`} className="w-full text-left p-4 flex justify-between items-center gap-4 hover:bg-white/[0.02]">
                      <span className="font-medium">{f.question}</span>
                      <span className="text-[#0891B2] text-xl leading-none shrink-0">{isOpen ? "−" : "+"}</span>
                    </button>
                    {isOpen && <div className="p-4 pt-0 tl-prose text-sm" dangerouslySetInnerHTML={{ __html: sanitize(f.answer) }} />}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/* ─── Legal page: /legal/:slug ─────────────────────────────────────── */
export function LegalPage() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setErr(false); setDoc(null); api.get(`/legal/${slug}`).then((r) => setDoc(r.data)).catch(() => setErr(true)); }, [slug]);
  if (err) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><SEOHead title="Not found" /><h1 className="text-2xl font-display font-bold mb-3">Not found</h1><p className="text-[#475569]">This document isn't published yet.</p><Link to="/" className="tl-btn-primary inline-flex mt-6">Home</Link></div>;
  if (!doc) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-[#475569]">Loading…</div>;
  const canonical = `${window.location.origin}/legal/${doc.slug}`;
  return (
    <article data-testid="legal-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEOHead title={doc.seo_title || doc.title} description={doc.seo_description || doc.summary} canonical={canonical} />
      <div className="tl-label mb-2">Legal</div>
      <h1 data-testid="legal-title" className="text-3xl sm:text-4xl font-display font-bold mb-4">{doc.title}</h1>
      <div className="flex flex-wrap items-center gap-4 text-xs text-[#475569] mb-8 pb-6 border-b border-slate-200">
        <span>Version {doc.version}</span>
        {doc.effective_date && <span>Effective {new Date(doc.effective_date).toLocaleDateString()}</span>}
        {doc.updated_at && <span>Last updated {new Date(doc.updated_at).toLocaleDateString()}</span>}
      </div>
      {doc.summary && <p className="text-[#475569] italic mb-6">{doc.summary}</p>}
      <div data-testid="legal-content" className="tl-prose" dangerouslySetInnerHTML={{ __html: sanitize(doc.content) }} />
    </article>
  );
}
